import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import posthog from 'posthog-js';
import { getProduct } from '../../services/products';
import { formatCurrency } from '../../utils/formatters';
import { useCart, MAX_SAMPLE_ITEMS } from '../../context/CartContext';
import { SAMPLE_ELIGIBLE_UNITS } from '../../utils/constants';
import GrowerMap from '../../components/products/GrowerMap';
import useRequireCompleteProfile from '../../hooks/useRequireCompleteProfile';
import './ProductDetail.css';

const UNIT_ORDER = { '18 oz': 1.125, '1 lb': 1, '2 lb': 2, '5 lb': 5, '10 lb': 10, '30 lb': 30, '50 lb': 50 };

/* ── Sourcing notes shown on every PDP (not product-specific) ── */
const SOURCING_NOTES =
  'Sourced from partner farms we know and trust. Grown without synthetic pesticides ' +
  'or fertilizers, then stone-milled fresh to preserve natural flavor, nutrients, and character.';

const ProductDetail = () => {
  useRequireCompleteProfile();
  const { id } = useParams();
  const { addItem, items: cartItems, sampleItemCount } = useCart();
  const sampleAtMax = sampleItemCount >= MAX_SAMPLE_ITEMS;
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [added, setAdded] = useState({});
  const [addedSample, setAddedSample] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getProduct(parseInt(id, 10));
        setProduct({
          ...data,
          variants: (data.variants || [])
            .filter(v => v.is_active !== false)
            .sort((a, b) => (UNIT_ORDER[a.unit] ?? 99) - (UNIT_ORDER[b.unit] ?? 99)),
        });
        posthog.capture('product_viewed', {
          product_id: data.id,
          product_name: data.name,
        });
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="pdp pdp--state">
        <p className="pdp__state-text">Loading…</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pdp pdp--state">
        <p className="pdp__state-text">{error?.message || 'Product not found.'}</p>
        <Link to="/products" className="pdp__back">← Back to Products</Link>
      </div>
    );
  }

  const growers = product.growers || [];
  const grainVarieties = product.grain_varieties || [];
  const isOutOfStock = product.inventory_status === 'out_of_stock';
  const isLowInventory = product.inventory_status === 'low_inventory';

  /* Build a deduplicated list of regions from the growers */
  const regions = [...new Set(growers.map(g => g.region))].join(', ');

  const getQty = (variantId) => quantities[variantId] ?? 1;

  const setQty = (variantId, val) => {
    setQuantities(prev => ({ ...prev, [variantId]: Math.max(1, val) }));
  };

  const handleAddToCart = (variant) => {
    addItem({
      productVariantId: variant.id,
      quantity: getQty(variant.id),
      product,
      variant,
    });
    posthog.capture('add_to_cart', {
      product_id: product.id,
      product_name: product.name,
      variant_id: variant.id,
      variant_name: variant.variant_name,
      unit: variant.unit,
      price: variant.price_dollars ?? variant.price,
      quantity: getQty(variant.id),
    });
    setAdded(prev => ({ ...prev, [variant.id]: true }));
    setTimeout(() => setAdded(prev => ({ ...prev, [variant.id]: false })), 1800);
  };

  const handleAddSample = (variant) => {
    addItem({
      productVariantId: variant.id,
      quantity: 1,
      product,
      variant,
      is_sample: true,
    });
    posthog.capture('sample_requested', {
      product_id: product.id,
      product_name: product.name,
      variant_id: variant.id,
      variant_name: variant.variant_name,
      unit: variant.unit,
    });
    setAddedSample(prev => ({ ...prev, [variant.id]: true }));
    setTimeout(() => setAddedSample(prev => ({ ...prev, [variant.id]: false })), 1800);
  };

  return (
    <div className="pdp">

      {/* ── Banner ─────────────────────────────────────────── */}
      <div className="pdp__banner">
        <div className="pdp__banner-inner">
          <Link to="/products" className="pdp__back">← Products</Link>
          <p className="pdp__eyebrow">Deep Roots Milling — Stone Milled Grain</p>
          <h1 className="pdp__name">{product.name}</h1>
          {isOutOfStock && (
            <span className="pdp__inventory-badge pdp__inventory-badge--out">Out of Stock</span>
          )}
          {isLowInventory && (
            <span className="pdp__inventory-badge pdp__inventory-badge--low">Low Inventory</span>
          )}
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────── */}
      <div className="pdp__content">

        {/* Product image */}
        {product.image_url && (
          <div className="pdp__image-wrap">
            <img src={product.image_url} alt={product.name} className="pdp__image" />
          </div>
        )}

        {/* Description */}
        {product.description && (
          <section className="pdp__section">
            <p className="pdp__description">{product.description}</p>
          </section>
        )}

        {/* Origin & Sourcing */}
        {growers.length > 0 && (
          <section className="pdp__section">
            <div className="pdp__section-header">
              <p className="pdp__section-eyebrow">Origin & Sourcing</p>
              <div className="pdp__rule" />
            </div>

            <GrowerMap growers={growers} />

            <div className="pdp__facts">
              {regions && (
                <div className="pdp__fact">
                  <span className="pdp__fact-label">Region</span>
                  <span className="pdp__fact-value">{regions}</span>
                </div>
              )}
              {grainVarieties.length > 0 && (
                <div className="pdp__fact">
                  <span className="pdp__fact-label">Grain Variet{grainVarieties.length > 1 ? 'ies' : 'y'}</span>
                  <span className="pdp__fact-value">
                    {grainVarieties.map(v => v.name).join(', ')}
                  </span>
                </div>
              )}
              <div className="pdp__fact">
                <span className="pdp__fact-label">Milling</span>
                <span className="pdp__fact-value">Stone-milled in small batches</span>
              </div>
            </div>

            <p className="pdp__sourcing-notes">{SOURCING_NOTES}</p>

            {/* Grower cards */}
            <div className="pdp__growers">
              {growers.map(g => (
                <div key={g.id} className="pdp__grower-card">
                  <span className="pdp__grower-name">{g.farm_name}</span>
                  <span className="pdp__grower-region">{g.region}</span>
                  {g.certifications?.length > 0 && (
                    <span className="pdp__grower-certs">
                      {g.certifications.map(c => c.replace(/_/g, ' ')).join(' · ')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Available sizes */}
        {product.variants.length > 0 && (
          <section className="pdp__section">
            <div className="pdp__section-header">
              <p className="pdp__section-eyebrow">Available Sizes</p>
              <div className="pdp__rule" />
            </div>

            <div className="pdp__variants">
              {product.variants.map(variant => (
                <div key={variant.id} className="pdp__variant-row">
                  <div className="pdp__variant-info">
                    <span className="pdp__variant-name">{variant.variant_name}</span>
                    {variant.sku && (
                      <span className="pdp__variant-sku">SKU: {variant.sku}</span>
                    )}
                    <span className="pdp__variant-price">
                      {formatCurrency(variant.price_dollars ?? variant.price)}
                      {variant.unit && (
                        <span className="pdp__variant-unit"> / {variant.unit}</span>
                      )}
                    </span>
                  </div>
                  <div className="pdp__variant-actions">
                    {!isOutOfStock && (
                      <div className="pdp__variant-qty">
                        <button
                          className="pdp__qty-btn"
                          onClick={() => setQty(variant.id, getQty(variant.id) - 1)}
                          aria-label="Decrease quantity"
                        >−</button>
                        <span className="pdp__qty-value">{getQty(variant.id)}</span>
                        <button
                          className="pdp__qty-btn"
                          onClick={() => setQty(variant.id, getQty(variant.id) + 1)}
                          aria-label="Increase quantity"
                        >+</button>
                      </div>
                    )}
                    <button
                      className={`pdp__add-btn${added[variant.id] ? ' pdp__add-btn--added' : ''}${isOutOfStock ? ' pdp__add-btn--unavailable' : ''}`}
                      onClick={() => !isOutOfStock && handleAddToCart(variant)}
                      disabled={isOutOfStock}
                    >
                      {isOutOfStock ? 'Out of Stock' : added[variant.id] ? 'Added ✓' : 'Add to Order'}
                    </button>
                    {SAMPLE_ELIGIBLE_UNITS.has(variant.unit) && (() => {
                      const alreadySampled = cartItems.some(i => i.productVariantId === variant.id && i.is_sample);
                      const isAdded = addedSample[variant.id] || alreadySampled;
                      const disabled = sampleAtMax && !alreadySampled;
                      return (
                        <button
                          className={`pdp__sample-btn${isAdded ? ' pdp__sample-btn--added' : ''}${disabled ? ' pdp__sample-btn--disabled' : ''}`}
                          onClick={() => !disabled && handleAddSample(variant)}
                          disabled={disabled}
                          title={disabled ? `Max ${MAX_SAMPLE_ITEMS} samples per order` : 'Add a complimentary sample (no charge)'}
                        >
                          {isAdded ? 'Added ✓' : disabled ? `Max ${MAX_SAMPLE_ITEMS} reached` : 'Request Sample'}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="pdp__cta">
          <Link to="/products" className="pdp__cta-back">← Products</Link>
          {cartItems.length > 0 && (() => {
            const totalItems = cartItems.reduce((s, i) => s + i.quantity, 0);
            return (
              <Link to="/order/new" className="pdp__cta-link">
                Review Order ({totalItems} item{totalItems !== 1 ? 's' : ''}) →
              </Link>
            );
          })()}
        </div>

      </div>
    </div>
  );
};

export default ProductDetail;
